import React, { useCallback, useEffect, useRef, useState } from "react";
import { Klydo } from "../../../../Types";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSTextField from "../../../system-design/DSTextField";
import DSDialogContent from "../../../system-design/DSDialogContent";
import KlydoCard from "./KlydoCard";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import { debounce } from "lodash";
import useSealedKlydos, {
  SearchType,
} from "../../../../components/hooks/useSealedKlydos";
import DSGrid from "../../../system-design/DSGrid";
import DSListItem from "../../../system-design/DSListItem";
import DSList from "../../../system-design/DSList";
import DSBox from "../../../system-design/DSBox";
import DSTypography from "../../../system-design/DSTypography";
import DSStack from "../../../system-design/DSStack";
import DSSwitch from "../../../system-design/DSSwitch";
import DSMenuItem from "../../../system-design/DSMenuItem";
import { KlydoSearchFilter, KlydoType } from "../../../../api/klydo";
import useGetKlydosByIds from "../../../Device/hooks/useGetKlydosByIds";
import { isSealedKlydoType } from "../../../../utils/validatePendingKlydos";

interface KlydosPickDialogProps {
  onClose: () => void;
  selectedKlydos: string[];
  onPickKlydos: (selectedKlydos: string[]) => void;
  limit?: number;
  greet?: boolean;
  publicOnly?: boolean;
  /** Restrict the results to one klydo type (e.g. Sealed when the caller
   * cannot accept drafts). Omit to search all types. */
  klydoType?: KlydoType;
  /** Overrides the dialog title text (defaults to "Klydos"). */
  title?: string;
  /** Optional node rendered in the dialog title, to the right of the title
   * text — e.g. an inherit-from-firmware dropdown. */
  headerRight?: React.ReactNode;
  /** Label for the confirm button (defaults to "Save"). */
  saveLabel?: string;
}

const PAGE_SIZE = 20;

const KlydosPickDialog: React.FC<KlydosPickDialogProps> = ({
  onClose,
  selectedKlydos,
  onPickKlydos,
  limit,
  greet,
  publicOnly,
  klydoType,
  title,
  headerRight,
  saveLabel,
}) => {
  type TriState = "ALL" | "TRUE" | "FALSE";
  const [selected, setSelected] = useState<string[]>(selectedKlydos);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [greetFilter, setGreetFilter] = useState<TriState>(
    greet === true ? "TRUE" : "ALL",
  );
  const [publicFilter, setPublicFilter] = useState<TriState>("ALL");
  const [collectionFilter, setCollectionFilter] = useState<TriState>("ALL");
  const [searchType, setSearchType] = useState<SearchType>("TEXT");

  const buildFilters = (): KlydoSearchFilter => {
    const filters: KlydoSearchFilter = {};
    if (publicOnly) {
      filters.isPublic = true;
      return filters;
    }
    if (greetFilter !== "ALL") filters.greet = greetFilter === "TRUE";
    if (publicFilter !== "ALL") filters.isPublic = publicFilter === "TRUE";
    if (collectionFilter !== "ALL")
      filters.inCollection = collectionFilter === "TRUE";
    return filters;
  };

  const activeFilters = buildFilters();

  const {
    data: klydos,
    dataRef: klydosRef,
    isFetching,
    fetchNextPage,
    isLoading,
  } = useSealedKlydos(
    textSearch,
    PAGE_SIZE,
    searchType,
    activeFilters,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    klydoType,
  );

  // Klydos admitted outside the server search (single-id lookup, pasted id
  // lists) bypass the search filters, so re-apply the caller's constraints
  // client-side with the SAME predicates the server search uses:
  // - the type restriction, via the shared isSealedKlydoType (legacy
  //   'ApprovedKlydo' documents count as sealed — the send validator uses the
  //   same predicate, so the picker never offers what the add then refuses);
  // - every active KlydoSearchFilter field buildFilters() can produce
  //   (publicOnly folds into isPublic there).
  const matchesTypeRestriction = (k: { type: string }): boolean =>
    !klydoType ||
    (klydoType === KlydoType.Sealed
      ? isSealedKlydoType(k.type)
      : k.type === klydoType);
  const matchesActiveFilters = (k: {
    greet?: boolean;
    public?: { status?: boolean };
    collectionId?: string;
  }): boolean =>
    (activeFilters.isPublic === undefined ||
      (k.public?.status === true) === activeFilters.isPublic) &&
    (activeFilters.greet === undefined ||
      (k.greet ?? false) === activeFilters.greet) &&
    (activeFilters.inCollection === undefined ||
      !!k.collectionId === activeFilters.inCollection);

  // The text search matches names/labels, never ids — so a single typed or
  // pasted id found nothing and gave no feedback. Look an id-shaped query up
  // by id as well, and merge the hit into the list.
  const idQuery = textSearch.trim();
  const isIdLikeQuery = /^[A-Za-z0-9_-]{12,}$/.test(idQuery);
  const {
    klydos: idMatches,
    isLoading: isIdLookupLoading,
    fetchKlydosByIds,
  } = useGetKlydosByIds(isIdLikeQuery ? [idQuery] : []);
  // Exact-id match only: the query hook keeps previous data as placeholder,
  // so without this a stale hit for the PREVIOUS id would render (and be
  // selectable) under the current query.
  const idHit = isIdLikeQuery
    ? idMatches.find((k) => k.id === idQuery)
    : undefined;
  const idLookupPending = isIdLikeQuery && isIdLookupLoading;
  const idHitAllowed =
    !!idHit && matchesTypeRestriction(idHit) && matchesActiveFilters(idHit);
  const idBlockReason: "type" | "public" | "filters" | null =
    idHit && !idHitAllowed
      ? !matchesTypeRestriction(idHit)
        ? "type"
        : publicOnly && idHit.public?.status !== true
          ? "public"
          : "filters"
      : null;
  const displayedKlydos = [
    ...(idHit && idHitAllowed && !klydos?.some((k) => k?.id === idHit.id)
      ? [idHit]
      : []),
    ...(klydos ?? []),
  ];

  const handleFreeSearch = useCallback(
    debounce((s) => setTextSearch(s), 500),
    [],
  );

  const handleToggleSelection = (id: string) => {
    if (limit && selected.length >= limit && !selected.includes(id)) {
      return;
    }
    setSelected((prev) => {
      const index = prev.findIndex((item) => item === id);
      if (index > -1) {
        return prev.filter((item) => item !== id);
      } else {
        const klydoToAdd = displayedKlydos.find((klydo) => klydo?.id === id);
        return klydoToAdd ? [...prev, klydoToAdd.id] : prev;
      }
    });
  };

  const loadedIds: string[] = displayedKlydos.map((k) => k?.id).filter(Boolean);
  const allLoadedSelected =
    loadedIds.length > 0 && loadedIds.every((id) => selected.includes(id));

  const applyLimit = (ids: string[]): string[] =>
    limit ? ids.slice(0, limit) : ids;

  const handleToggleSelectAllLoaded = () => {
    if (allLoadedSelected) {
      setSelected((prev) => prev.filter((id) => !loadedIds.includes(id)));
    } else {
      setSelected((prev) =>
        applyLimit(Array.from(new Set([...prev, ...loadedIds]))),
      );
    }
  };

  const parseIdList = (text: string): string[] => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v).trim()).filter(Boolean);
        }
      } catch {
        // not valid JSON; fall through
      }
    }
    if (!/[,\n]/.test(trimmed)) return [];
    return trimmed
      .split(/[\s,]+/)
      .map((s) => s.replace(/^["']|["']$/g, "").trim())
      .filter(Boolean);
  };

  const lastHandledPasteRef = useRef<string>("");
  const [lastPasteAddedCount, setLastPasteAddedCount] = useState<number>(0);
  const [pasteBlockedNotice, setPasteBlockedNotice] = useState<string | null>(
    null,
  );

  // Pasted ids must pass the same gate as everything else in the list —
  // adding them to the selection unchecked would bypass the type/public/
  // filter constraints the caller relies on (and admit ids that don't exist).
  const handlePastedIds = async (ids: string[]) => {
    try {
      const found = await fetchKlydosByIds(ids);
      const allowedIds = new Set(
        found
          .filter((k) => matchesTypeRestriction(k) && matchesActiveFilters(k))
          .map((k) => k.id),
      );
      const blockedCount = ids.filter((id) => !allowedIds.has(id)).length;
      setSelected((prev) => {
        const merged = applyLimit(
          Array.from(
            new Set([...prev, ...ids.filter((id) => allowedIds.has(id))]),
          ),
        );
        setLastPasteAddedCount(merged.length - prev.length);
        return merged;
      });
      setPasteBlockedNotice(
        blockedCount > 0
          ? `${blockedCount} pasted id(s) skipped — not found or not allowed here`
          : null,
      );
    } catch {
      setLastPasteAddedCount(0);
      setPasteBlockedNotice("Could not validate the pasted ids — none added");
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    const ids = parseIdList(value);
    if (ids.length > 0) {
      if (lastHandledPasteRef.current !== value) {
        lastHandledPasteRef.current = value;
        handlePastedIds(ids);
      }
      handleFreeSearch.cancel();
      setIsSearching(false);
      return;
    }
    if (value.trim().startsWith("[")) {
      handleFreeSearch.cancel();
      setIsSearching(false);
      return;
    }
    lastHandledPasteRef.current = "";
    setLastPasteAddedCount(0);
    setPasteBlockedNotice(null);
    setIsSearching(true);
    handleFreeSearch(value);
  };

  const handleScroll = () => {
    if (
      listRef.current &&
      listRef.current.scrollTop + listRef.current.clientHeight >=
        listRef.current.scrollHeight - 100 &&
      !isFetching &&
      !isLoading
    ) {
      setCurrentIndex((prev) => prev + 4);
    }
  };

  const handleCancel = () => {
    setTextSearch("");
    setSelected(selectedKlydos);
    onClose();
  };

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    onPickKlydos(selected);
    onClose();
  };

  useEffect(() => {
    if (klydos) {
      setIsSearching(false);
    }
  }, [klydos]);

  useEffect(() => {
    if (
      klydosRef.current &&
      currentIndex >= klydosRef.current.length - (PAGE_SIZE - 1) &&
      !isFetching
    ) {
      fetchNextPage();
    }
  }, [currentIndex, isFetching]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [textSearch]);

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <DSDialog open={true} onClose={handleCancel} fullWidth maxWidth="md">
      <DSDialogTitle>
        <DSBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <span>{title ?? "Klydos"}</span>
          {headerRight}
        </DSBox>
        <DSTextField
          fullWidth
          autoFocus
          aria-invalid={isSearching}
          value={searchInputValue}
          onChange={(e) => handleSearchInputChange(e.target.value)}
          placeholder="Search klydos, or paste IDs (JSON array or comma/newline list)"
        />
        <DSStack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
          <DSTypography>Text</DSTypography>
          <DSSwitch
            value={searchType === "TEXT"}
            onClick={() => {
              setSearchType((prev) =>
                prev === "TEXT" ? "SIMILARITY" : "TEXT",
              );
            }}
          />
          <DSTypography>Semantic</DSTypography>
        </DSStack>
        <DSBox
          display={publicOnly ? "none" : "flex"}
          justifyContent="flex-start"
          flexWrap="wrap"
          gap={2}
          sx={{ marginTop: "10px" }}
        >
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSTypography variant="caption">Greet:</DSTypography>
            <DSTextField
              select
              size="small"
              sx={{ width: "100px" }}
              value={greetFilter}
              onChange={(e) => setGreetFilter(e.target.value as TriState)}
            >
              <DSMenuItem value="ALL">All</DSMenuItem>
              <DSMenuItem value="TRUE">Yes</DSMenuItem>
              <DSMenuItem value="FALSE">No</DSMenuItem>
            </DSTextField>
          </DSBox>
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSTypography variant="caption">Public:</DSTypography>
            <DSTextField
              select
              size="small"
              sx={{ width: "100px" }}
              value={publicFilter}
              onChange={(e) => setPublicFilter(e.target.value as TriState)}
            >
              <DSMenuItem value="ALL">All</DSMenuItem>
              <DSMenuItem value="TRUE">Yes</DSMenuItem>
              <DSMenuItem value="FALSE">No</DSMenuItem>
            </DSTextField>
          </DSBox>
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSTypography variant="caption">In Collection:</DSTypography>
            <DSTextField
              select
              size="small"
              sx={{ width: "120px" }}
              value={collectionFilter}
              onChange={(e) => setCollectionFilter(e.target.value as TriState)}
            >
              <DSMenuItem value="ALL">All</DSMenuItem>
              <DSMenuItem value="TRUE">Yes</DSMenuItem>
              <DSMenuItem value="FALSE">No</DSMenuItem>
            </DSTextField>
          </DSBox>
        </DSBox>
        <DSStack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", marginTop: "10px" }}
        >
          <DSSecondaryButton
            onClick={handleToggleSelectAllLoaded}
            disabled={loadedIds.length === 0}
          >
            {allLoadedSelected
              ? "Deselect all loaded"
              : `Select all loaded (${loadedIds.length})`}
          </DSSecondaryButton>
          <DSTypography variant="caption">
            {`Selected: ${selected.length}${limit ? ` / ${limit}` : ""}`}
          </DSTypography>
          {lastPasteAddedCount > 0 && (
            <DSTypography variant="caption" color="success.main">
              {`+${lastPasteAddedCount} from paste`}
            </DSTypography>
          )}
          {pasteBlockedNotice && (
            <DSTypography variant="caption" color="error.main">
              {pasteBlockedNotice}
            </DSTypography>
          )}
        </DSStack>
      </DSDialogTitle>
      <DSDialogContent dividers style={{ height: "80vh", overflow: "hidden" }}>
        {!isSearching && klydos && !isLoading && !idLookupPending ? (
          <DSList ref={listRef} style={{ height: "100%", overflow: "auto" }}>
            {displayedKlydos.length === 0 && (
              <DSBox sx={{ textAlign: "center", p: 3 }}>
                <DSTypography color="text.secondary">
                  {idBlockReason === "type"
                    ? "This id is not a sealed klydo — only sealed klydos can be picked here."
                    : idBlockReason === "public"
                      ? "This id is not a public klydo — only public klydos can be picked here."
                      : idBlockReason === "filters"
                        ? "A klydo matches this id but is hidden by the active filters."
                        : isIdLikeQuery
                          ? "No klydo matches this search or id."
                          : "No klydos match this search."}
                </DSTypography>
              </DSBox>
            )}
            <DSGrid container spacing={1}>
              {displayedKlydos.map((klydo: Klydo) => (
                <DSGrid item key={klydo?.id}>
                  <DSListItem disablePadding>
                    <KlydoCard
                      klydoId={klydo.id}
                      isSelected={selected.includes(klydo?.id)}
                      onToggleSelection={() => handleToggleSelection(klydo?.id)}
                    />
                  </DSListItem>
                </DSGrid>
              ))}
            </DSGrid>
          </DSList>
        ) : (
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "calc(100vh - 100px)",
              minHeight: "200px",
              flexDirection: "column",
            }}
          >
            <DSCircularProgress />
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave}>
          {saveLabel ?? "Save"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default KlydosPickDialog;
